import { Modal } from "@legal-500/legal500-design-system";
import { Link } from "react-router-dom";
import type { ChangeAuditEntry, ChangeAuditSource } from "@/api/changeAudit.service";
import { ActionPill, ActorCell, actionLabel, ENTITY_PAGE, formatWhen } from "./auditCells";
import { FromTo } from "./auditDetailValues";
import styles from "./AuditLog.module.css";
import { ResearchEntryDetail, ResearchSummary } from "./ResearchEntryDetail";

type AuditEntryDetailProps = {
  entry: ChangeAuditEntry | null;
  source: ChangeAuditSource;
  onClose: () => void;
};

export function AuditEntryDetail({ entry, source, onClose }: AuditEntryDetailProps) {
  if (!entry) return null;

  const changes = entry.changes ? Object.entries(entry.changes) : [];
  const page = ENTITY_PAGE[source];
  const isResearch = source === "research-cms";

  return (
    <Modal isOpen onClose={onClose} title="Change details" size="large">
      <div className={styles.detail}>
        <dl className={styles.summary}>
          <dt>When</dt>
          <dd>{formatWhen(entry.occurredAt)}</dd>
          <dt>Who</dt>
          <dd>
            <ActorCell entry={entry} />
          </dd>
          <dt>Action</dt>
          <dd>
            <ActionPill action={actionLabel(source, entry.action)} />
          </dd>
          {isResearch ? (
            <ResearchSummary entry={entry} />
          ) : (
            <>
              <dt>Firm</dt>
              <dd>{entry.firmName ?? "—"}</dd>
              <dt>Publication</dt>
              <dd>{entry.publicationName ?? "—"}</dd>
              <dt>Lawyer</dt>
              <dd>{entry.lawyerName ?? "—"}</dd>
              <dt>Entity id</dt>
              <dd>
                <span className={styles.mono}>{entry.entityId}</span>
              </dd>
              <dt>External id</dt>
              <dd>
                {entry.externalId ? <span className={styles.mono}>{entry.externalId}</span> : "—"}
              </dd>
            </>
          )}
        </dl>

        {page && (
          <Link to={page.path(entry.entityId)} className={styles.openEntityLink}>
            Open {page.label}
          </Link>
        )}

        {isResearch ? (
          <ResearchEntryDetail entry={entry} />
        ) : (
          <>
            <section aria-labelledby="audit-changes-heading">
              <h3 id="audit-changes-heading" className={styles.sectionHeading}>
                Changed fields
              </h3>
              {changes.length === 0 ? (
                <p className={styles.muted}>This entry records no field changes.</p>
              ) : (
                changes.map(([field, change]) => (
                  <FromTo key={field} field={field} from={change.from} to={change.to} />
                ))
              )}
            </section>

            <section aria-labelledby="audit-metadata-heading">
              <h3 id="audit-metadata-heading" className={styles.sectionHeading}>
                Metadata
              </h3>
              {entry.metadata && Object.keys(entry.metadata).length > 0 ? (
                <pre className={styles.json}>{JSON.stringify(entry.metadata, null, 2)}</pre>
              ) : (
                <p className={styles.muted}>No metadata.</p>
              )}
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}
